// v2 beaver — a proper waddling beaver: a chunky wedge body rising to a high
// rounded rump, a blunt-muzzled head with the two orange incisors showing,
// small ears, stubby legs with paw balls, and the signature paddle tail — a
// broad flat oval slab on a furred base, dragging just off the ground. Two
// coats: dark chocolate and russet. Ported 1:1 from age-of-mcp's v2/beaver.ts,
// using the library's shared `wetland` (eyes, quadLegs) helpers.
import * as THREE from "three";
import { defineModel, wetland, type Part } from "../../lib";

const COATS = [
  { rump: "#5a3e26", chest: "#684a2e", head: "#6e4e30", muzzle: "#7e5c3a",
    leg: "#46301e", tail: "#3c3028" },
  { rump: "#74502e", chest: "#825c38", head: "#8a623c", muzzle: "#9a7248",
    leg: "#5a3e26", tail: "#4a3a30" },
];

function beaverParts(v: number): Part[] {
  const c = COATS[v % COATS.length]!;
  const parts: Part[] = wetland.quadLegs(0.026, 0.026, 0.048, 0.0065, c.leg,
    { paw: 0.006, pawColor: c.leg });
  parts.push(
    // high rounded rump sloping down to the chest — the beaver wedge
    { geometry: new THREE.IcosahedronGeometry(0.034, 0), color: c.rump,
      position: [-0.018, 0.048, 0], scale: [1.25, 1.1, 1.1] },
    { geometry: new THREE.IcosahedronGeometry(0.027, 0), color: c.chest,
      position: [0.025, 0.042, 0], scale: [1.1, 1.0, 1.0] },
    // blunt head with muzzle, nose, ears
    { geometry: new THREE.IcosahedronGeometry(0.019, 0), color: c.head,
      position: [0.052, 0.058, 0], scale: [1.15, 0.95, 0.95] },
    { geometry: new THREE.IcosahedronGeometry(0.011, 0), color: c.muzzle,
      position: [0.068, 0.05, 0], scale: [1.0, 0.8, 1.0] },
    { geometry: new THREE.IcosahedronGeometry(0.0035, 0), color: "#241a10",
      position: [0.077, 0.054, 0] },
    { geometry: new THREE.IcosahedronGeometry(0.005, 0), color: c.leg,
      position: [0.046, 0.073, 0.014] },
    { geometry: new THREE.IcosahedronGeometry(0.005, 0), color: c.leg,
      position: [0.046, 0.073, -0.014] },
    // the two orange incisors
    { geometry: new THREE.BoxGeometry(0.003, 0.009, 0.0035), color: "#e8a020",
      position: [0.0725, 0.042, 0.0025] },
    { geometry: new THREE.BoxGeometry(0.003, 0.009, 0.0035), color: "#e8a020",
      position: [0.0725, 0.042, -0.0025] },
    ...wetland.eyes(0.06, 0.064, 0.0125, 0.003),
    // furred tail base, then the broad flat paddle dragging low
    { geometry: new THREE.BoxGeometry(0.02, 0.008, 0.016), color: c.rump,
      position: [-0.052, 0.032, 0], rotation: [0, 0, 0.15] },
    { geometry: new THREE.IcosahedronGeometry(0.026, 0), color: c.tail,
      position: [-0.085, 0.02, 0], scale: [1.85, 0.2, 1.3],
      rotation: [0, 0, 0.08] },
  );
  return parts;
}

export const beaverV2 = defineModel({
  id: "beaver-v2",
  batchId: "fable",
  name: "Beaver (v2)",
  tags: ["creature","beaver","low-poly","age-of-mcp","fable","water"],
  summary: "Low-poly beaver.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.09,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => beaverParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => beaverParts(1) },
  ],
  animations: [],
});
